import { useInfiniteQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  InteractionListResponseSchema,
  InteractionSchema,
  type CreateInteractionInput,
  type DeleteInteractionInput,
  type Interaction,
  type PatchInteractionInput,
} from "@/api/types/interaction";
import type { LogCallInput } from "@/api/types/voice";

export const interactionKeys = {
  all: (contactId: string | undefined) => ["contacts", contactId, "interactions"] as const,
  list: (contactId: string | undefined, limit: number) =>
    [...interactionKeys.all(contactId), "list", { limit }] as const,
};

function invalidateContact(queryClient: QueryClient, contactId: string) {
  // Interactions feed both the interactions list AND the overview's merged
  // timeline — invalidate both so "Log interaction" shows up in the
  // timeline without a manual page refresh.
  void queryClient.invalidateQueries({ queryKey: interactionKeys.all(contactId) });
  void queryClient.invalidateQueries({ queryKey: ["contacts", contactId, "overview"] });
}

/** Cursor-paginated interaction list — mirrors useContacts'/useReviews' convention. */
export function useInteractions(contactId: string | undefined, limit = 25) {
  return useInfiniteQuery({
    queryKey: interactionKeys.list(contactId, limit),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(
        `/v2/contacts/${contactId}/interactions?${searchParams.toString()}`,
      );
      return validateResponse(
        InteractionListResponseSchema,
        data,
        "GET /v2/contacts/:id/interactions",
      );
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: !!contactId,
    staleTime: 15 * 1000,
  });
}

export function useCreateInteraction() {
  const queryClient = useQueryClient();
  return useMutation<Interaction, unknown, CreateInteractionInput>({
    mutationFn: async ({ contactId, ...body }) => {
      const { data } = await apiClient.post(`/v2/contacts/${contactId}/interactions`, body);
      return validateResponse(
        InteractionSchema,
        data,
        "POST /v2/contacts/:id/interactions",
      );
    },
    onSuccess: (_result, variables) => {
      invalidateContact(queryClient, variables.contactId);
    },
  });
}

export function usePatchInteraction() {
  const queryClient = useQueryClient();
  return useMutation<Interaction, unknown, PatchInteractionInput>({
    mutationFn: async ({ contactId, interactionId, ...patch }) => {
      const { data } = await apiClient.patch(
        `/v2/contacts/${contactId}/interactions/${interactionId}`,
        patch,
      );
      return validateResponse(
        InteractionSchema,
        data,
        "PATCH /v2/contacts/:id/interactions/:iid",
      );
    },
    onSuccess: (_result, variables) => {
      invalidateContact(queryClient, variables.contactId);
    },
  });
}

export interface SendContactEmailInput {
  contactId: string;
  subject: string;
  body_html: string;
}

/**
 * POST /api/v2/contacts/{id}/email — compose + send an outbound email to the
 * contact. Returns a Phase-10-shaped Interaction (type=email,
 * direction=outbound) so it slots straight into the same timeline as
 * manually-logged interactions; invalidating the same keys as
 * useCreateInteraction makes the new row show up in History on success.
 * 422s (contact has no email) surface via getErrorMessage in the caller.
 */
export function useSendContactEmail() {
  const queryClient = useQueryClient();
  return useMutation<Interaction, unknown, SendContactEmailInput>({
    mutationFn: async ({ contactId, subject, body_html }) => {
      const { data } = await apiClient.post(`/v2/contacts/${contactId}/email`, {
        subject,
        body_html,
      });
      return validateResponse(InteractionSchema, data, "POST /v2/contacts/:id/email");
    },
    onSuccess: (_result, variables) => {
      invalidateContact(queryClient, variables.contactId);
    },
  });
}

export interface SendContactSmsInput {
  contactId: string;
  body: string;
}

/**
 * POST /api/v2/contacts/{id}/sms — compose + send an outbound text to the
 * contact. Returns a Phase-10-shaped Interaction (type=text,
 * direction=outbound) so it slots straight into the same timeline as
 * manually-logged interactions. 422 (no phone on file) and 403 (no
 * sms_consent) surface via getErrorMessage in the caller — same pattern as
 * useSendContactEmail.
 */
export function useSendSms() {
  const queryClient = useQueryClient();
  return useMutation<Interaction, unknown, SendContactSmsInput>({
    mutationFn: async ({ contactId, body }) => {
      const { data } = await apiClient.post(`/v2/contacts/${contactId}/sms`, { body });
      return validateResponse(InteractionSchema, data, "POST /v2/contacts/:id/sms");
    },
    onSuccess: (_result, variables) => {
      invalidateContact(queryClient, variables.contactId);
    },
  });
}

/**
 * POST /api/v2/contacts/{id}/call/log {status?, duration_sec?} — Phase 15.
 * Fired once an in-browser Twilio Voice SDK call ends (see
 * src/hooks/useVoice.ts), so it slots into the same History timeline as
 * every other logged touch. Replaces the Phase 12 `useCallContact`
 * click-to-call bridge (POST /v2/contacts/{id}/call, which rang Chris's
 * phone first) — that flow is retired on the web side; calling now happens
 * entirely in-browser.
 */
export function useLogCall() {
  const queryClient = useQueryClient();
  return useMutation<Interaction, unknown, LogCallInput>({
    mutationFn: async ({ contactId, ...body }) => {
      const { data } = await apiClient.post(`/v2/contacts/${contactId}/call/log`, body);
      return validateResponse(InteractionSchema, data, "POST /v2/contacts/:id/call/log");
    },
    onSuccess: (_result, variables) => {
      invalidateContact(queryClient, variables.contactId);
    },
  });
}

export function useDeleteInteraction() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, DeleteInteractionInput>({
    mutationFn: async ({ contactId, interactionId }) => {
      await apiClient.delete(`/v2/contacts/${contactId}/interactions/${interactionId}`);
    },
    onSuccess: (_result, variables) => {
      invalidateContact(queryClient, variables.contactId);
    },
  });
}
